import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

interface FormData {
  name: string;
  email: string;
}

// Access environment variables
const CONVERTKIT_API_SECRET = process.env.REACT_APP_CONVERTKIT_API_SECRET;
const CONVERTKIT_FORM_ID = process.env.REACT_APP_CONVERTKIT_FORM_ID;

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const formRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debug log on component mount
  useEffect(() => {
    console.log('ConvertKit Configuration:', {
      hasApiSecret: !!CONVERTKIT_API_SECRET,
      hasFormId: !!CONVERTKIT_FORM_ID
    });
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTimeout(() => {
      window.scrollTo({
        top: formRef.current?.offsetTop! - 80,
        behavior: 'smooth'
      });
    }, 100);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    console.log('Environment Variables Check:', {
      hasApiSecret: !!process.env.REACT_APP_CONVERTKIT_API_SECRET,
      hasFormId: !!process.env.REACT_APP_CONVERTKIT_FORM_ID,
      formId: process.env.REACT_APP_CONVERTKIT_FORM_ID
    });

    if (!process.env.REACT_APP_CONVERTKIT_API_SECRET || !process.env.REACT_APP_CONVERTKIT_FORM_ID) {
      setError('Configuration error. Please contact support.');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`https://api.convertkit.com/v3/forms/${process.env.REACT_APP_CONVERTKIT_FORM_ID}/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          api_secret: process.env.REACT_APP_CONVERTKIT_API_SECRET,
          email: formData.email,
          first_name: formData.name,
        }),
      });

      const data = await response.json();
      console.log('Full ConvertKit Response:', {
        status: response.status,
        statusText: response.statusText,
        data: data,
        requestBody: {
          api_secret: process.env.REACT_APP_CONVERTKIT_API_SECRET ? 'present' : 'missing',
          form_id: process.env.REACT_APP_CONVERTKIT_FORM_ID,
          email: formData.email,
          first_name: formData.name,
        }
      });

      if (!response.ok) {
        throw new Error(data.message || 'Failed to subscribe');
      }

      // Store user data in localStorage
      localStorage.setItem('kloudskool_user', JSON.stringify({
        name: formData.name,
        email: formData.email,
        registeredAt: new Date().toISOString()
      }));

      // On successful submission
      setFormData({ name: '', email: '' }); // Clear the form
      navigate('/confirm-email', { 
        state: { 
          email: formData.email,
          name: formData.name 
        } 
      });
      
    } catch (err: any) {
      console.error('Submission error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-8 pb-12 px-4 bg-gradient-to-br from-[#F8FAFC] via-white to-[#F0F9FF]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative mb-8">
            {/* Left Column - Text */}
            <div className="flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1C98ED]/5 rounded-full text-[#1C98ED] font-medium text-sm mb-4">
                <span className="w-1.5 h-1.5 bg-[#1C98ED] rounded-full animate-pulse"></span>
                Now Enrolling for 2025
              </div>
              <h1 className="text-4xl md:text-5xl leading-[1.2] font-bold text-[#15213A] mb-3">
                Master <span className="text-[#1C98ED]">Cloud</span> Engineering
              </h1>
              <p className="text-base md:text-lg text-gray-600 mb-4 leading-relaxed">
                Go from zero to cloud engineer with a job-ready portfolio, real projects and gain skills employers expect on day one.
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-[#F9943B]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Self-paced learning</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-[#F9943B]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Expert guidance</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-[#F9943B]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Hands-on projects</span>
                </div>
              </div>
            </div>
            
            {/* Right Column - Form */}
            <div ref={formRef} className="bg-white rounded-xl p-6 shadow-lg">
              <div className="absolute -top-2 -right-2 bg-[#F9943B] text-white text-xs font-medium px-3 py-1 rounded-full shadow-sm">
                Limited Spots
              </div>
              <h3 className="text-xl font-bold text-[#15213A] mb-4">Start Your Journey</h3>
              <form onSubmit={handleSubscribe} className="space-y-3">
                  <div className="relative">
                    <input 
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your name" 
                    className="w-full px-3 py-2 bg-[#F8FAFC] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1C98ED] focus:border-[#1C98ED] transition-all text-sm"
                      required
                    disabled={isSubmitting}
                    />
                  </div>
                  <div className="relative">
                    <input 
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Your email" 
                    className="w-full px-3 py-2 bg-[#F8FAFC] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1C98ED] focus:border-[#1C98ED] transition-all text-sm"
                      required
                    disabled={isSubmitting}
                    />
                </div>
                {error && (
                  <div className="text-red-500 text-sm text-center">
                    {error}
                  </div>
                )}
                  <button 
                    type="submit"
                  className="w-full bg-[#1C98ED] text-white font-medium py-2 rounded-lg hover:bg-[#1C98ED]/90 transition-colors text-sm disabled:opacity-50"
                  disabled={isSubmitting}
                  >
                  {isSubmitting ? 'Processing...' : 'Start Learning'}
                  </button>
                </form>
              <div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-gray-500">
                <svg className="w-3.5 h-3.5 text-[#F9943B]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span>Your information is secure and encrypted</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Companies Section */}
      <section className="py-6 bg-[#F8FAFC]">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-4">
            <h3 className="text-xl font-bold text-[#15213A]">Our students learn the exact skills used by engineers at companies like</h3>
          </div>
          <div className="relative w-full overflow-hidden">
            <div className="flex w-[200%] animate-scroll">
              {/* First set of logos */}
              <div className="flex w-1/2 justify-around items-center gap-8">
                <img 
                  src={`${process.env.PUBLIC_URL}/SSE_plc_2021_logo.svg.png`} 
                  alt="SSE" 
                  className="h-8 object-contain grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
                />
                <img 
                  src={`${process.env.PUBLIC_URL}/PricewaterhouseCoopers_Logo.svg.png`} 
                  alt="PwC" 
                  className="h-8 object-contain grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
                />
                <img 
                  src={`${process.env.PUBLIC_URL}/M&S_logo.svg.png`} 
                  alt="M&S" 
                  className="h-8 object-contain grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
                />
              </div>
              {/* Second set of logos */}
              <div className="flex w-1/2 justify-around items-center gap-8">
                <img 
                  src={`${process.env.PUBLIC_URL}/SSE_plc_2021_logo.svg.png`} 
                  alt="SSE" 
                  className="h-8 object-contain grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
                />
                <img 
                  src={`${process.env.PUBLIC_URL}/PricewaterhouseCoopers_Logo.svg.png`} 
                  alt="PwC" 
                  className="h-8 object-contain grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
                />
                <img 
                  src={`${process.env.PUBLIC_URL}/M&S_logo.svg.png`} 
                  alt="M&S" 
                  className="h-8 object-contain grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-white">
        {/* Structured Learning Modules */}
        <div className="py-16 border-b border-gray-100">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-[#15213A]">You don’t need another course. You need proof you can do the job.</h3>
                <p className="text-gray-600">Kloud Skool isn’t just about learning, it’s about showing what you can do.</p>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="mt-1 flex-shrink-0 text-xl">
                      💻
                    </div>
                    <div>
                      <h4 className="font-medium text-[#15213A]">Industry-Standard Tools</h4>
                      <p className="text-sm text-gray-600 mt-1">Use VS Code, Linux, GitHub, Azure, Terraform and CICD Pipelines like a real engineer</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="mt-1 flex-shrink-0 text-xl">
                      🛠️
                    </div>
                    <div>
                      <h4 className="font-medium text-[#15213A]">Project-based training</h4>
                      <p className="text-sm text-gray-600 mt-1">Build secure, production grade infrastructure, not just tutorials</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="mt-1 flex-shrink-0 text-xl">
                      📈
                    </div>
                    <div>
                      <h4 className="font-medium text-[#15213A]">Job-ready outcomes</h4>
                      <p className="text-sm text-gray-600 mt-1">Leave with projects that prove your skills to employers</p>
                    </div>
                  </li>
                </ul>
                <div className="flex gap-3">
                  <button onClick={scrollToForm} className="bg-[#15213A] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#15213A]/90 transition-colors w-full">
                    Start Learning
                  </button>
                </div>
              </div>
              <div>
                <img 
                  src={`${process.env.PUBLIC_URL}/1.svg`}
                  alt="👨‍💻 Student Learning Dashboard"
                  className="w-[400px] h-[600px] object-contain mx-auto"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Level Up System */}
        <div className="py-16 bg-[#F8FAFC] border-b border-gray-100">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <img 
                  src={`${process.env.PUBLIC_URL}/4.svg`}
                  alt="🔒 Module Progression System"
                  className="w-[400px] h-[600px] object-contain mx-auto"
                />
              </div>
              <div className="space-y-6 order-1 lg:order-2">
                <h3 className="text-2xl font-bold text-[#15213A]">You don’t need endless content. You need a clear path to progress</h3>
                <p className="text-gray-600">Kloud Skool helps you actually move forward, not just collect lessons.</p>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="mt-1 flex-shrink-0 text-xl">
                      🎯
                    </div>
                    <div>
                      <h4 className="font-medium text-[#15213A]">Clear structure, real momentum</h4>
                      <p className="text-sm text-gray-600 mt-1">Know exactly what to do next and why it matters</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="mt-1 flex-shrink-0 text-xl">
                      🏆
                    </div>
                    <div>
                      <h4 className="font-medium text-[#15213A]">Earn your progress</h4>
                      <p className="text-sm text-gray-600 mt-1">Unlock new content by mastering real-world tasks</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="mt-1 flex-shrink-0 text-xl">
                      ⚡
                    </div>
                    <div>
                      <h4 className="font-medium text-[#15213A]">Stay motivated with visible wins</h4>
                      <p className="text-sm text-gray-600 mt-1">Track your growth and celebrate milestones that reflect actual skill</p>
                    </div>
                  </li>
                </ul>
                <div className="flex gap-3">
                  <button onClick={scrollToForm} className="bg-[#15213A] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#15213A]/90 transition-colors w-full">
                    Start Learning
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Interactive Quizzes */}
        <div className="py-16 border-b border-gray-100">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-[#15213A]">You don’t need another quiz. You need to prove you can do the job</h3>
                <p className="text-gray-600">At Kloud Skool, every module ends with a challenge that puts your skills to the test.</p>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="mt-1 flex-shrink-0 text-xl">
                      📝
                    </div>
                    <div>
                      <h4 className="font-medium text-[#15213A]">Hands-on exercises</h4>
                      <p className="text-sm text-gray-600 mt-1">Apply what you’ve learned by building and debugging real infrastructure</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="mt-1 flex-shrink-0 text-xl">
                      🎯
                    </div>
                    <div>
                      <h4 className="font-medium text-[#15213A]">Real-World Scenarios</h4>
                      <p className="text-sm text-gray-600 mt-1">Solve practical problems that mirror actual cloud engineering challenges</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="mt-1 flex-shrink-0 text-xl">
                      👥
                    </div>
                    <div>
                      <h4 className="font-medium text-[#15213A]">Community Learning</h4>
                      <p className="text-sm text-gray-600 mt-1">Learn from others' questions and share your own insights in discussion threads</p>
                    </div>
                  </li>
                </ul>
                <div className="flex gap-3">
                  <button onClick={scrollToForm} className="bg-[#15213A] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#15213A]/90 transition-colors w-full">
                    Start Learning
                  </button>
                </div>
              </div>
              <div>
                <img 
                  src={`${process.env.PUBLIC_URL}/3.svg`}
                  alt="📝 Module Assessment"
                  className="w-[400px] h-[600px] object-contain mx-auto"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Video Tutorials */}
        <div className="py-16 bg-[#F8FAFC] border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <img 
                  src={`${process.env.PUBLIC_URL}/2.svg`}
                  alt="🎥 Real-World Cloud Engineering Demo"
                  className="w-[400px] h-[600px] object-contain mx-auto"
                />
              </div>
              <div className="space-y-6 order-1 lg:order-2">
                <h3 className="text-2xl font-bold text-[#15213A]">Skip the theory dumps. Learn by doing, with engineers who’ve been there.</h3>
                <p className="text-gray-600">At Kloud Skool, you don’t just watch someone else code, you learn by replicating, debugging, and deploying it yourself.</p>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="mt-1 flex-shrink-0 text-xl">
                      🎯
                    </div>
                    <div>
                      <h4 className="font-medium text-[#15213A]">Real-world walkthroughs</h4>
                      <p className="text-sm text-gray-600 mt-1">Watch engineers solve problems they’ve faced in actual projects</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="mt-1 flex-shrink-0 text-xl">
                      🔄
                    </div>
                    <div>
                      <h4 className="font-medium text-[#15213A]">End-to-end builds</h4>
                      <p className="text-sm text-gray-600 mt-1">Follow full deployments from config to infrastructure and apps</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="mt-1 flex-shrink-0 text-xl">
                      📚
                    </div>
                    <div>
                      <h4 className="font-medium text-[#15213A]">Apply what you watch</h4>
                      <p className="text-sm text-gray-600 mt-1">Pause, rewind, and replicate everything in your own Azure lab</p>
                    </div>
                  </li>
                </ul>
                <div className="flex gap-3">
                  <button onClick={scrollToForm} className="bg-[#15213A] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#15213A]/90 transition-colors w-full">
                    Start Learning
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Leaderboard */}
        <div className="py-16">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-[#15213A]">Leaderboard and XP System</h3>
                <p className="text-gray-600">Climb the ranks as you master cloud engineering skills.</p>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="mt-1 flex-shrink-0 text-xl">
                      ⭐
                    </div>
                    <div>
                      <h4 className="font-medium text-[#15213A]">Earn While You Learn</h4>
                      <p className="text-sm text-gray-600 mt-1">Gain XP points for completing lessons, quizzes, and hands-on labs</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="mt-1 flex-shrink-0 text-xl">
                      🏆
                    </div>
                    <div>
                      <h4 className="font-medium text-[#15213A]">Compete and Excel</h4>
                      <p className="text-sm text-gray-600 mt-1">Rise through the ranks and showcase your dedication to learning</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="mt-1 flex-shrink-0 text-xl">
                      🚀
                    </div>
                    <div>
                      <h4 className="font-medium text-[#15213A]">Stay Motivated</h4>
                      <p className="text-sm text-gray-600 mt-1">Challenge yourself and others in a friendly learning environment</p>
                    </div>
                  </li>
                </ul>
                <div className="flex gap-3">
                  <button onClick={scrollToForm} className="bg-[#15213A] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#15213A]/90 transition-colors w-full">
                    Start Learning
                  </button>
                </div>
              </div>
              <div>
                <img 
                  src={`${process.env.PUBLIC_URL}/5.svg`}
                  alt="📊 Cloud Engineer Rankings"
                  className="w-[400px] h-[600px] object-contain mx-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Journey to Cloud Engineering Section */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#15213A] mb-4">Your Journey to Cloud Engineering</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Build real-world cloud projects as you progress from IT fundamentals to advanced cloud engineering. Graduate with a portfolio of practical solutions that employers value.</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
            <div className="space-y-6">
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="mt-1 flex-shrink-0 text-xl">
                    🚀
                  </div>
                  <div>
                    <h4 className="font-medium text-[#15213A]">Foundation (4-6 weeks)</h4>
                    <p className="text-sm text-gray-600 mt-1">Master essential development tools like VS Code, understand version control with Git, and build a strong Linux foundation. Project: Set up a development environment and version control workflow.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="mt-1 flex-shrink-0 text-xl">
                    ☁️
                  </div>
                  <div>
                    <h4 className="font-medium text-[#15213A]">Cloud Fundamentals (6-8 weeks)</h4>
                    <p className="text-sm text-gray-600 mt-1">Master Azure through hands-on projects: Deploy scalable web apps, configure cloud networking, implement security best practices, and set up monitoring. Build a production-ready cloud infrastructure that showcases real-world architectural patterns.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="mt-1 flex-shrink-0 text-xl">
                    🔧
                  </div>
                  <div>
                    <h4 className="font-medium text-[#15213A]">Infrastructure as Code (4-6 weeks)</h4>
                    <p className="text-sm text-gray-600 mt-1">Automate cloud infrastructure deployment using modern IaC tools and practices. Project: Build a complete cloud environment using Terraform and Azure.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="mt-1 flex-shrink-0 text-xl">
                    🔄
                  </div>
                  <div>
                    <h4 className="font-medium text-[#15213A]">CI/CD & DevOps (4-6 weeks)</h4>
                    <p className="text-sm text-gray-600 mt-1">Build automated pipelines and implement DevOps practices. Project: Create a full CI/CD pipeline for automated testing and deployment.</p>
                  </div>
                </li>
              </ul>
              <div className="flex gap-3">
                <button onClick={scrollToForm} className="bg-[#15213A] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#15213A]/90 transition-colors w-full">
                  Start Learning
                </button>
              </div>
            </div>
            <div>
              <img 
                src={`${process.env.PUBLIC_URL}/journey.svg`}
                alt="Cloud Engineering Learning Path"
                className="w-full h-auto max-w-xl mx-auto"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="text-2xl mb-3">🏗️</div>
              <h4 className="font-medium text-[#15213A] mb-2">Real Projects</h4>
              <p className="text-sm text-gray-600">Build actual cloud solutions that you can showcase to employers</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="text-2xl mb-3">⏱️</div>
              <h4 className="font-medium text-[#15213A] mb-2">Self-Paced</h4>
              <p className="text-sm text-gray-600">Learn at your own speed with lifetime access</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="text-2xl mb-3">💼</div>
              <h4 className="font-medium text-[#15213A] mb-2">Portfolio Building</h4>
              <p className="text-sm text-gray-600">Graduate with a portfolio of completed cloud projects</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="text-2xl mb-3">🎯</div>
              <h4 className="font-medium text-[#15213A] mb-2">Industry-Ready</h4>
              <p className="text-sm text-gray-600">Graduate with skills valued by top employers</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-8 bg-gradient-to-br from-[#F8FAFC] via-white to-[#F0F9FF]">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <div className="max-w-2xl mx-auto text-center mb-6">
              <h2 className="text-2xl font-bold text-[#15213A] mb-2">
                Ready to Transform Your Career?
              </h2>
              <p className="text-base text-gray-600">
              Join many aspiring and like-minded engineers who are looking to transform their career through cloud engineering.
              </p>
            </div>

            <div className="max-w-md mx-auto">
              <form onSubmit={handleSubscribe} className="space-y-3">
                <div className="relative">
                  <input 
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your name" 
                    className="w-full px-3 py-2 bg-[#F8FAFC] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1C98ED] focus:border-[#1C98ED] transition-all text-sm"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div className="relative">
                  <input 
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Your email" 
                    className="w-full px-3 py-2 bg-[#F8FAFC] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1C98ED] focus:border-[#1C98ED] transition-all text-sm"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                {error && (
                  <div className="text-red-500 text-sm text-center">
                    {error}
                  </div>
                )}
                <button 
                  type="submit"
                  className="w-full bg-[#1C98ED] text-white font-medium py-2 rounded-lg hover:bg-[#1C98ED]/90 transition-colors text-sm disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Processing...' : 'Start Your Cloud Journey Today'}
                </button>
              </form>
              <div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-gray-500">
                <svg className="w-3.5 h-3.5 text-[#1C98ED]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <p className="text-gray-600">
                  14-day money-back guarantee. No questions asked.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Animations */}
      <style>
        {`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        .animate-scroll {
          animation: scroll 30s linear infinite;
        }
        `}
      </style>
      <Footer />
    </div>
  );
};

export default HomePage; 